import { isEmptyString } from '@/utils/string';
import { isAbsoluteHttpUrl } from '@/utils/url';
import type { BucketConfigDraft, BucketConfigField } from '@/features/warehouse-sync';
import type { BucketConfigMode, BucketConfigValidator } from './types';
import type { WarehouseDestination } from '@/services/db/warehouse-sync/entity';

const BLANK = "can't be blank";
const INVALID_JSON = 'must be valid JSON';
const INVALID_URL = 'must be a valid URL';

/**
 * What a blank secret means on an update: the value was never sent to the browser,
 * so it has to be typed again rather than being "missing".
 */
const REENTER_SECRET: Readonly<Record<WarehouseDestination, string>> = Object.freeze({
  s3: 'Enter your secret key again to update the S3 bucket configuration',
  gcs: 'Enter your service account key again to update the GCS bucket configuration',
  azure_blob: 'Enter your shared access token again to update the Azure blob configuration',
  yandex: 'Enter your secret key again to update the Yandex bucket configuration',
});

type FieldErrors = Partial<Record<BucketConfigField, string>>;

/** Records a message only when there is one, so "no key" keeps meaning "valid". */
function addError(errors: FieldErrors, field: BucketConfigField, message: string | null): void {
  if (message != null) {
    errors[field] = message;
  }
}

/** `null` when present. `updateMessage` replaces the generic copy on an update. */
function requiredMessage(value: string | undefined, mode: BucketConfigMode, updateMessage?: string): string | null {
  if (!isEmptyString(value)) {
    return null;
  }
  return mode === 'update' && updateMessage != null ? updateMessage : BLANK;
}

function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * S3 and Yandex share a field set — both speak the S3 API — and differ only in
 * which product the re-entry copy names.
 */
function validateAccessKeyPairConfig(
  config: BucketConfigDraft,
  mode: BucketConfigMode,
  secretKeyUpdateMessage: string,
) {
  const errors: FieldErrors = {};
  addError(errors, 'bucket_name', requiredMessage(config.bucket_name, mode));
  addError(errors, 'access_key', requiredMessage(config.access_key, mode));
  addError(errors, 'secret_key', requiredMessage(config.secret_key, mode, secretKeyUpdateMessage));
  addError(errors, 'region', requiredMessage(config.region, mode));
  return errors;
}

const validateS3Config: BucketConfigValidator = (config, mode) =>
  validateAccessKeyPairConfig(config, mode, REENTER_SECRET.s3);

const validateYandexConfig: BucketConfigValidator = (config, mode) =>
  validateAccessKeyPairConfig(config, mode, REENTER_SECRET.yandex);

const validateGcsConfig: BucketConfigValidator = (config, mode) => {
  const errors: FieldErrors = {};
  addError(errors, 'bucket_name', requiredMessage(config.bucket_name, mode));

  const credentials = config.credentials;
  // The blank check wins: on an update an empty box means "re-enter it", never
  // that the service account key was malformed.
  const missing = requiredMessage(credentials, mode, REENTER_SECRET.gcs);
  if (missing != null) {
    errors.credentials = missing;
  } else if (credentials != null && !isValidJson(credentials)) {
    errors.credentials = INVALID_JSON;
  }
  return errors;
};

const validateAzureBlobConfig: BucketConfigValidator = (config, mode) => {
  const errors: FieldErrors = {};

  const sasUrl = config.container_sas_url;
  const missing = requiredMessage(sasUrl, mode, REENTER_SECRET.azure_blob);
  if (missing != null) {
    errors.container_sas_url = missing;
  } else if (sasUrl != null && !isAbsoluteHttpUrl(sasUrl)) {
    // The server parses this with no base of its own, so a bare host is not a URL.
    errors.container_sas_url = INVALID_URL;
  }
  return errors;
};

/**
 * `bucket_path` has no validator for any provider — it is optional everywhere, and
 * only ever carries a message the server sent back.
 */
export const PROVIDER_VALIDATORS: Readonly<Record<WarehouseDestination, BucketConfigValidator>> = Object.freeze({
  s3: validateS3Config,
  gcs: validateGcsConfig,
  yandex: validateYandexConfig,
  azure_blob: validateAzureBlobConfig,
});
